import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Clock,
  Download,
  Mail,
  Plus,
  RefreshCw,
  Send,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import Toast from '../../components/Toast';
import Nr2jPlanFollowUpModal from '../../components/nr2j/Nr2jPlanFollowUpModal';
import Nr2jContinueModal from '../../components/nr2j/Nr2jContinueModal.jsx';
import { useNr2jContinueAction } from '../../hooks/useNr2jContinueAction.js';
import { useToast } from '../../hooks/useToast';
import {
  NR2J_CONTINUE_INTENTS,
  preflightQuoteSend,
  preflightQuoteToContract,
  preflightQuoteToInvoice,
  requirementsFromError,
} from '../../utils/nr2jContinuePreflight.js';
import apiService from '../../services/api';
import {
  NR2J_CATALOG_BILLING_TYPES,
  NR2J_CONVERSION_MISSING_LABELS,
  NR2J_QUOTE_ORG_GATE_KEYS,
  NR2J_QUOTE_SEND_GATE_LABELS,
  computeQuoteOrgBannerMissingKeys,
  NR2J_VAT_OPTIONS,
  buildOrgFicheUrlFromConversion,
  buildOrgFicheUrlFromQuoteGate,
  centsToEuros,
  computeOrgSendGateMissing,
  computeQuoteLineTotals,
  effectiveStatus,
  eurosToCents,
  formatCentsAsEuros,
  formatNr2jDate,
  isQuoteFullyDraftDeletable,
  nr2jBillingPeriodSuffix,
  nr2jBillingTypeLabel,
  nr2jDiscountScopeLabel,
  nr2jEmailLogStatusLabel,
  nr2jQuoteStatusBadge,
  organizationDisplayName,
  contactDisplayName,
} from '../../utils/nr2jConstants';
import { useNr2jAutoRefresh } from '../../hooks/useNr2jAutoRefresh';
import Nr2jQuoteLivePreviewPanel from '../../components/nr2j/Nr2jQuoteLivePreviewPanel.jsx';
import { notifyNr2jDataChanged } from '../../utils/nr2jRealtime.js';
import {
  nr2jQuoteSilentReloadFormAction,
  serializeNr2jQuoteEditableForm,
} from '../../utils/nr2jQuoteFormDirty.js';
import {
  approvalErrorMessage,
  approvalUserName,
  isApprovalRequiredError,
} from '../../utils/nr2jApprovalUi.js';

function emptyLine() {
  return {
    key: `${Date.now()}-${Math.random()}`,
    catalogItemId: '',
    name: '',
    description: '',
    quantity: 1,
    unitPriceHtCents: 0,
    priceEuros: '',
    vatRateBps: 2000,
    billingType: 'ONE_TIME',
    discountMode: 'none',
    discountPercent: '',
    discountEuros: '',
  };
}

function lineFromApi(line) {
  let discountMode = 'none';
  let discountPercent = '';
  let discountEuros = '';
  if (line.discountPercentBps != null && line.discountPercentBps !== '') {
    discountMode = 'percent';
    discountPercent = String(Number(line.discountPercentBps) / 100);
  } else if (line.discountAmountHtCents != null && line.discountAmountHtCents !== '') {
    discountMode = 'fixed';
    discountEuros = centsToEuros(line.discountAmountHtCents);
  }
  return {
    key: line.id || `${Date.now()}-${Math.random()}`,
    catalogItemId: line.catalogItemId || '',
    name: line.name || '',
    description: line.description || '',
    quantity: line.quantity || 1,
    unitPriceHtCents: line.unitPriceHtCents || 0,
    priceEuros: centsToEuros(line.unitPriceHtCents),
    vatRateBps: line.vatRateBps ?? 2000,
    billingType: line.billingType || 'ONE_TIME',
    discountMode,
    discountPercent,
    discountEuros,
  };
}

function lineToPayload(line, index) {
  const unitPriceHtCents = line.priceEuros !== ''
    ? eurosToCents(line.priceEuros)
    : Number(line.unitPriceHtCents) || 0;
  const payload = {
    catalogItemId: line.catalogItemId || null,
    sortOrder: index,
    name: line.name.trim(),
    description: line.description?.trim() || null,
    quantity: Math.max(1, Number(line.quantity) || 1),
    unitPriceHtCents,
    vatRateBps: Number(line.vatRateBps),
    billingType: line.billingType,
    discountPercentBps: null,
    discountAmountHtCents: null,
  };
  if (line.discountMode === 'percent' && line.discountPercent !== '') {
    payload.discountPercentBps = Math.round(Number(line.discountPercent) * 100);
  } else if (line.discountMode === 'fixed' && line.discountEuros !== '') {
    payload.discountAmountHtCents = eurosToCents(line.discountEuros);
  }
  return payload;
}

function lineForTotals(line) {
  const payload = lineToPayload(line, 0);
  return payload;
}

function findVersion(quote, versionId) {
  if (!quote || !versionId) return null;
  if (quote.currentVersion?.id === versionId) return quote.currentVersion;
  return (quote.versions || []).find((v) => v.id === versionId) || null;
}

/** Bouton « Créer la facture » : aligné sur le permit backend (OR). */
function sessionCanCreateInvoiceFromQuote() {
  try {
    const raw = localStorage.getItem('admin_user');
    const user = raw ? JSON.parse(raw) : null;
    if (!user) return false;
    if (user.role === 'SUPER_ADMIN') return true;
    const perms = new Set(Array.isArray(user.platformPermissions) ? user.platformPermissions : []);
    return (
      perms.has('NR2J_QUOTES_EDIT')
      || perms.has('NR2J_INVOICES_EDIT')
      || perms.has('NR2J_INVOICES_CREATE')
    );
  } catch {
    return false;
  }
}

/** Bouton « Créer le contrat » depuis devis accepté. */
function sessionCanCreateContractFromQuote() {
  try {
    const raw = localStorage.getItem('admin_user');
    const user = raw ? JSON.parse(raw) : null;
    if (!user) return false;
    if (user.role === 'SUPER_ADMIN') return true;
    const perms = new Set(Array.isArray(user.platformPermissions) ? user.platformPermissions : []);
    return perms.has('NR2J_CONTRACTS_EDIT') || perms.has('NR2J_QUOTES_EDIT');
  } catch {
    return false;
  }
}

export default function Nr2jQuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();
  const {
    continueReq,
    openContinue,
    closeContinue,
    runContinueRetry,
    withActionLock,
  } = useNr2jContinueAction();
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [org, setOrg] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const selectedVersionIdRef = useRef('');
  selectedVersionIdRef.current = selectedVersionId;
  const [lines, setLines] = useState([]);
  const [contactId, setContactId] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [clientNote, setClientNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [sendGateOpen, setSendGateOpen] = useState(false);
  const [sendGateMissing, setSendGateMissing] = useState([]);
  const [persistentMissing, setPersistentMissing] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [decisionOpen, setDecisionOpen] = useState(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpConflict, setFollowUpConflict] = useState(null);
  const [followUpBusy, setFollowUpBusy] = useState(false);
  const [linkedInvoice, setLinkedInvoice] = useState(null);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [linkedContract, setLinkedContract] = useState(null);
  const [contractBusy, setContractBusy] = useState(false);
  const formBaselineRef = useRef('');
  const linesRef = useRef(lines);
  const contactIdRef = useRef(contactId);
  const validUntilRef = useRef(validUntil);
  const clientNoteRef = useRef(clientNote);
  const internalNoteRef = useRef(internalNote);
  linesRef.current = lines;
  contactIdRef.current = contactId;
  validUntilRef.current = validUntil;
  clientNoteRef.current = clientNote;
  internalNoteRef.current = internalNote;

  const currentVersion = quote?.currentVersion;
  const version = findVersion(quote, selectedVersionId) || currentVersion;
  const isHistoricalView = Boolean(version?.id && currentVersion?.id && version.id !== currentVersion.id);
  const effStatus = version ? (version.effectiveStatus || effectiveStatus(version)) : null;
  const isDraft = version?.status === 'DRAFT';
  const isSent = version?.status === 'SENT';
  const isAccepted = version?.status === 'ACCEPTED';
  const isRefused = version?.status === 'REFUSED';
  const badge = nr2jQuoteStatusBadge(effStatus);

  const currentEffStatus = currentVersion
    ? (currentVersion.effectiveStatus || effectiveStatus(currentVersion))
    : null;
  const currentIsSent = currentVersion?.status === 'SENT';
  const currentIsAccepted = currentVersion?.status === 'ACCEPTED';
  const showDecisionActions = !isHistoricalView && currentIsSent && version?.id === currentVersion?.id;
  const showCancelAction = !isHistoricalView
    && (currentIsSent || currentIsAccepted)
    && version?.id === currentVersion?.id;

  const approvalInfo = quote?.approval || null;
  const latestApproval = approvalInfo?.latest || null;
  const approvalStatus = latestApproval?.status || null;
  const needsApproval = Boolean(approvalInfo?.required);
  const approvalPending = approvalStatus === 'PENDING';
  const approvalGranted = approvalStatus === 'APPROVED';
  const canEdit = isDraft && !isHistoricalView && !(needsApproval && approvalPending);
  const canCreateInvoiceFromQuote = sessionCanCreateInvoiceFromQuote();
  const canCreateContractFromQuote = sessionCanCreateContractFromQuote();
  const showSendDraft = !needsApproval || approvalGranted;
  const showSubmitDraft = needsApproval && !approvalPending && !approvalGranted;
  const showApprovalBanner = Boolean(latestApproval) && isDraft && !isHistoricalView;

  const orgBannerMissing = useMemo(
    () => computeQuoteOrgBannerMissingKeys({
      persistentMissing,
      org,
      contacts: org?.contacts || [],
      isAccepted: currentIsAccepted && !isHistoricalView,
      hasLinkedInvoice: Boolean(linkedInvoice?.id),
    }),
    [persistentMissing, org, currentIsAccepted, isHistoricalView, linkedInvoice?.id],
  );
  const versionCount = quote?.versionCount ?? quote?.versions?.length ?? 0;
  const canDeleteQuote = quote ? isQuoteFullyDraftDeletable(quote) : false;
  const canDeleteDraftVersion = isDraft && versionCount > 1;

  function captureFormBaseline(next = {}) {
    formBaselineRef.current = serializeNr2jQuoteEditableForm({
      lines: next.lines ?? linesRef.current,
      contactId: next.contactId ?? contactIdRef.current,
      validUntil: next.validUntil ?? validUntilRef.current,
      clientNote: next.clientNote ?? clientNoteRef.current,
      internalNote: next.internalNote ?? internalNoteRef.current,
    });
  }

  function isEditableFormDirty() {
    if (!formBaselineRef.current) return false;
    return serializeNr2jQuoteEditableForm({
      lines: linesRef.current,
      contactId: contactIdRef.current,
      validUntil: validUntilRef.current,
      clientNote: clientNoteRef.current,
      internalNote: internalNoteRef.current,
    }) !== formBaselineRef.current;
  }

  function applyVersionToForm(v) {
    if (!v) return;
    const nextLines = (v.lines || []).length ? v.lines.map(lineFromApi) : [];
    const nextContactId = v.contactId || '';
    const nextValidUntil = v.validUntil ? new Date(v.validUntil).toISOString().slice(0, 10) : '';
    const nextClientNote = v.clientNote || '';
    const nextInternalNote = v.internalNote || '';
    setLines(nextLines);
    setContactId(nextContactId);
    setValidUntil(nextValidUntil);
    setClientNote(nextClientNote);
    setInternalNote(nextInternalNote);
    captureFormBaseline({
      lines: nextLines,
      contactId: nextContactId,
      validUntil: nextValidUntil,
      clientNote: nextClientNote,
      internalNote: nextInternalNote,
    });
  }

  function selectVersion(v) {
    if (!v) return;
    setSelectedVersionId(v.id);
    applyVersionToForm(v);
  }

  function applySendGateMissing(missing) {
    const list = Array.isArray(missing) ? missing : [];
    setSendGateMissing(list);
    setPersistentMissing(list);
    const nextErrors = {};
    if (list.includes('validUntil')) nextErrors.validUntil = true;
    if (list.includes('recipientEmail')) nextErrors.contact = true;
    setFieldErrors(nextErrors);
    setSendGateOpen(true);
  }

  const load = useCallback(async ({ silent = false, preferVersionId, applyForm = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [quoteRes, catalogRes] = await Promise.all([
        apiService.getNr2jQuote(id),
        apiService.listNr2jCatalog({ active: true }).catch(() => ({ items: [] })),
      ]);
      const q = quoteRes?.quote || quoteRes;
      setQuote(q);
      setCatalog(catalogRes?.items || []);
      const cv = q.currentVersion;
      const formDirty = isEditableFormDirty();
      const formAction = applyForm
        ? 'apply'
        : nr2jQuoteSilentReloadFormAction({ silent, formDirty });
      // Dirty local : mettre à jour métadonnées (quote/approval) sans écraser le formulaire.
      if (formAction === 'preserve') {
        /* keep selectedVersionId + lines / remises / notes */
      } else {
        let nextId = cv?.id || '';
        const preferred = preferVersionId ?? (silent ? selectedVersionIdRef.current : null);
        if (preferred && findVersion(q, preferred)) {
          nextId = preferred;
        }
        const selected = findVersion(q, nextId) || cv;
        if (selected) {
          setSelectedVersionId(selected.id);
          applyVersionToForm(selected);
        } else {
          setSelectedVersionId('');
        }
      }

      // Facture liée à la version courante ACCEPTED (évite doublon UI)
      if (cv?.status === 'ACCEPTED' && q.organizationId) {
        try {
          const invRes = await apiService.listNr2jInvoices({ organizationId: q.organizationId });
          const found = (invRes?.invoices || []).find(
            (inv) => inv.sourceQuoteVersionId === cv.id,
          );
          setLinkedInvoice(found || null);
        } catch {
          setLinkedInvoice(null);
        }
      } else {
        setLinkedInvoice(null);
      }
      if (q.organizationId) {
        const detail = await apiService.getNr2jOrganization(q.organizationId);
        setOrg(detail?.organization || detail);
      }
    } catch (error) {
      if (!silent) {
        toast.error(error?.message || 'Devis introuvable');
        navigate('/nr2j/devis');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const silentReload = useCallback(() => {
    load({ silent: true });
  }, [load]);
  // Polling / focus / visibility / bus NR2J — ne doit JAMAIS écraser un formulaire dirty.
  useNr2jAutoRefresh(silentReload, { enabled: Boolean(id), intervalMs: 12000, runOnMount: false });

  useEffect(() => {
    function revalidateOrgGate() {
      if (!quote?.organizationId) return;
      apiService.getNr2jOrganization(quote.organizationId)
        .then((detail) => {
          const nextOrg = detail?.organization || detail;
          setOrg(nextOrg);
          const contacts = detail?.contacts || nextOrg?.contacts || [];
          const stillMissing = computeOrgSendGateMissing(nextOrg, contacts);
          setPersistentMissing((prev) => {
            const editorOnly = (prev || []).filter((k) => !NR2J_QUOTE_ORG_GATE_KEYS.has(k));
            return [...new Set([...stillMissing, ...editorOnly])];
          });
        })
        .catch(() => {});
    }
    function onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      revalidateOrgGate();
    }
    function onFocus() {
      revalidateOrgGate();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [quote?.organizationId]);

  function addCatalogLine(itemId) {
    const item = catalog.find((c) => c.id === itemId);
    if (!item) return;
    setLines((prev) => [
      ...prev,
      {
        ...emptyLine(),
        catalogItemId: item.id,
        name: item.name,
        description: item.description || '',
        priceEuros: centsToEuros(item.unitPriceHtCents),
        unitPriceHtCents: item.unitPriceHtCents,
        vatRateBps: item.vatRateBps,
        billingType: item.billingType,
      },
    ]);
  }

  async function saveDraft(options = {}) {
    if (!canEdit || !version) return false;
    setSaving(true);
    try {
      const payload = {
        contactId: contactId || null,
        validUntil: validUntil ? new Date(validUntil).toISOString() : null,
        clientNote: clientNote || null,
        internalNote: internalNote || null,
        lines: lines.map(lineToPayload),
      };
      const res = await apiService.patchNr2jQuoteVersion(id, version.id, payload);
      setQuote(res?.quote || res);
      if (options.quiet !== true) toast.success('Brouillon enregistré');
      await load({ silent: true, applyForm: true });
      return true;
    } catch (error) {
      toast.error(error?.message || 'Enregistrement impossible');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function previewPdf() {
    if (!version) return;
    setActionBusy(true);
    try {
      if (canEdit) {
        const ok = await saveDraft();
        if (!ok) return;
      }
      await apiService.downloadNr2jQuotePdf(id, version.id, {
        number: quote?.number,
        version: version.version,
        mode: 'preview',
      });
    } catch (error) {
      toast.error(error?.message || 'Aperçu PDF impossible');
    } finally {
      setActionBusy(false);
    }
  }

  async function downloadPdf() {
    if (!version) return;
    setActionBusy(true);
    try {
      await apiService.downloadNr2jQuotePdf(id, version.id, {
        number: quote?.number,
        version: version.version,
      });
    } catch (error) {
      toast.error(error?.message || 'Téléchargement PDF impossible');
    } finally {
      setActionBusy(false);
    }
  }

  async function sendQuoteCore() {
    const res = await apiService.sendNr2jQuote(id);
    setQuote(res?.quote || res);
    notifyNr2jDataChanged({ entityType: 'QUOTE', action: 'SENT' });
    toast.success('Devis envoyé au client');
    setSendGateOpen(false);
    setPersistentMissing([]);
    setFieldErrors({});
    await load({ silent: true, applyForm: true });
  }

  function resumeSendQuoteFromContinuePayload(payload, ctx = {}) {
    const validUntilResume = payload?.validUntil
      ? new Date(payload.validUntil).toISOString().slice(0, 10)
      : ctx.validUntil;
    return sendQuote({
      fromContinue: true,
      org: payload?.org,
      contacts: payload?.contacts,
      validUntil: validUntilResume,
      contactId: payload?.quoteContactId ?? ctx.contactId,
      skipPreflight: true,
    });
  }

  async function sendQuote(options = {}) {
    const run = async () => {
      setActionBusy(true);
      try {
        if (canEdit && !options.fromContinue) {
          const ok = await saveDraft({ quiet: true });
          if (!ok) return;
        }
        const orgCtx = options.org ?? org;
        const contactsCtx = options.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
        const validUntilCtx = options.validUntil !== undefined ? options.validUntil : validUntil;
        const contactIdCtx = options.contactId !== undefined ? options.contactId : contactId;

        if (options.org) {
          setOrg((prev) => ({
            ...(prev || {}),
            ...options.org,
            contacts: options.contacts || prev?.contacts,
          }));
        }
        if (options.validUntil !== undefined) setValidUntil(validUntilCtx);
        if (options.contactId !== undefined) setContactId(contactIdCtx);

        if (!options.skipPreflight) {
          const pre = preflightQuoteSend({
            org: orgCtx,
            contacts: contactsCtx,
            validUntil: validUntilCtx,
            contactId: contactIdCtx,
          });
          if (pre) {
            openContinue({
              ...pre,
              quoteId: id,
              versionId: version?.id,
              quoteMeta: { validUntil: validUntilCtx, contactId: contactIdCtx },
            }, (payload) => resumeSendQuoteFromContinuePayload(payload, {
              validUntil: validUntilCtx,
              contactId: contactIdCtx,
            }));
            return;
          }
        }
        await sendQuoteCore();
      } catch (error) {
        const orgCtx = options.org ?? org;
        const contactsCtx = options.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
        const validUntilCtx = options.validUntil !== undefined ? options.validUntil : validUntil;
        const contactIdCtx = options.contactId !== undefined ? options.contactId : contactId;
        const req = requirementsFromError(error, {
          intent: NR2J_CONTINUE_INTENTS.QUOTE_SEND,
          org: orgCtx,
          contacts: contactsCtx,
          quoteId: id,
          versionId: version?.id,
          quoteMeta: { validUntil: validUntilCtx, contactId: contactIdCtx },
        });
        if (req) {
          openContinue(req, (payload) => resumeSendQuoteFromContinuePayload(payload, {
            validUntil: validUntilCtx,
            contactId: contactIdCtx,
          }));
          return;
        }
        if (isApprovalRequiredError(error)) {
          toast.error(approvalErrorMessage(error, 'Envoi impossible'));
          await load({ silent: true, applyForm: true });
        } else {
          toast.error(error?.message || 'Envoi impossible');
        }
      } finally {
        setActionBusy(false);
      }
    };
    if (options.fromContinue) {
      await run();
      return;
    }
    const { skipped } = await withActionLock(run);
    if (skipped) return;
  }

  async function submitForApproval() {
    setActionBusy(true);
    try {
      if (canEdit) {
        const ok = await saveDraft({ quiet: true });
        if (!ok) return;
      }
      const res = await apiService.submitNr2jQuote(id);
      notifyNr2jDataChanged({ entityType: 'QUOTE', action: 'SUBMITTED' });
      const approver = res?.approval?.assignedApprover;
      const approverName = !approver || approver.role === 'SUPER_ADMIN'
        ? "l'administrateur"
        : approvalUserName(approver, "l'administrateur");
      toast.success(`Devis soumis à ${approverName} pour validation.`);
      await load({ silent: true, applyForm: true });
    } catch (error) {
      toast.error(approvalErrorMessage(error, 'Soumission pour validation impossible'));
      await load({ silent: true, applyForm: true });
    } finally {
      setActionBusy(false);
    }
  }

  async function resendQuote() {
    setActionBusy(true);
    try {
      await apiService.sendNr2jQuote(id);
      toast.success('Devis renvoyé');
      await load({ silent: true, applyForm: true });
    } catch (error) {
      toast.error(error?.message || 'Renvoi impossible');
    } finally {
      setActionBusy(false);
    }
  }

  async function newVersion() {
    if (!window.confirm('Créer une nouvelle version brouillon à partir de la version actuelle ?')) {
      return;
    }
    setActionBusy(true);
    try {
      const res = await apiService.newNr2jQuoteVersion(id);
      const q = res?.quote || res;
      setQuote(q);
      toast.success('Nouvelle version créée');
      await load({ silent: true, applyForm: true, preferVersionId: q?.currentVersion?.id });
    } catch (error) {
      toast.error(error?.message || 'Impossible de créer une nouvelle version');
    } finally {
      setActionBusy(false);
    }
  }

  async function deleteQuote() {
    if (!canDeleteQuote) return;
    const confirmed = window.confirm(
      'Supprimer définitivement ce brouillon ?\nCette action ne supprimera aucun devis déjà envoyé.',
    );
    if (!confirmed) return;
    setActionBusy(true);
    try {
      await apiService.deleteNr2jQuote(id);
      toast.success('Devis supprimé');
      navigate('/nr2j/devis');
    } catch (error) {
      toast.error(error?.message || 'Suppression impossible');
    } finally {
      setActionBusy(false);
    }
  }

  async function deleteDraftVersion() {
    if (!canDeleteDraftVersion || !version) return;
    if (!window.confirm('Supprimer cette version brouillon ?')) return;
    setActionBusy(true);
    try {
      const res = await apiService.deleteNr2jQuoteVersion(id, version.id);
      if (res?.deleted) {
        toast.success('Devis supprimé');
        navigate('/nr2j/devis');
        return;
      }
      const q = res?.quote || res;
      setQuote(q);
      const cv = q?.currentVersion;
      if (cv) {
        setSelectedVersionId(cv.id);
        applyVersionToForm(cv);
      }
      toast.success('Version brouillon supprimée');
    } catch (error) {
      toast.error(error?.message || 'Suppression impossible');
    } finally {
      setActionBusy(false);
    }
  }

  async function submitDecision(type) {
    setActionBusy(true);
    try {
      const payload = { confirmed: true, decisionNote: decisionNote.trim() || null };
      let res;
      if (type === 'accept') res = await apiService.acceptNr2jQuote(id, payload);
      else if (type === 'refuse') res = await apiService.refuseNr2jQuote(id, payload);
      else res = await apiService.cancelNr2jQuote(id, payload);
      setQuote(res?.quote || res);
      setDecisionOpen(null);
      setDecisionNote('');
      toast.success(
        type === 'accept' ? 'Devis accepté' : type === 'refuse' ? 'Devis refusé' : 'Devis annulé',
      );
      await load({ silent: true, applyForm: true });
    } catch (error) {
      toast.error(error?.message || 'Action impossible');
    } finally {
      setActionBusy(false);
    }
  }

  async function createInvoiceFromQuoteCore() {
    const res = await apiService.createNr2jInvoiceFromQuote(id);
    const invoice = res?.invoice || res;
    toast.success(invoice.number ? `Facture créée — ${invoice.number}` : 'Facture brouillon créée');
    setLinkedInvoice(invoice);
    navigate(`/nr2j/facturation/${invoice.id}`);
  }

  async function createInvoiceFromQuote(options = {}) {
    if (linkedInvoice?.id && !options.fromContinue) {
      navigate(`/nr2j/facturation/${linkedInvoice.id}`);
      return;
    }
    const run = async () => {
      setInvoiceBusy(true);
      try {
        const orgCtx = options.org ?? org;
        const contactsCtx = options.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
        if (options.org) {
          setOrg((prev) => ({
            ...(prev || {}),
            ...options.org,
            contacts: options.contacts || prev?.contacts,
          }));
        }
        if (!options.skipPreflight) {
          const pre = preflightQuoteToInvoice({ org: orgCtx, contacts: contactsCtx });
          if (pre) {
            openContinue(
              { ...pre, quoteId: id },
              (payload) => createInvoiceFromQuote({
                fromContinue: true,
                org: payload?.org,
                contacts: payload?.contacts,
                skipPreflight: Boolean(payload?.org),
              }),
            );
            return;
          }
        }
        await createInvoiceFromQuoteCore();
      } catch (error) {
        const existingId =
          error?.data?.error?.details?.existingInvoiceId
          || error?.response?.data?.error?.details?.existingInvoiceId
          || error?.details?.existingInvoiceId;
        if (existingId) {
          toast.success('Facture déjà existante pour cette version');
          navigate(`/nr2j/facturation/${existingId}`);
          return;
        }
        const orgCtx = options.org ?? org;
        const contactsCtx = options.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
        const req = requirementsFromError(error, {
          intent: NR2J_CONTINUE_INTENTS.QUOTE_TO_INVOICE,
          org: orgCtx,
          contacts: contactsCtx,
          quoteId: id,
        });
        if (req) {
          openContinue(req, (payload) => createInvoiceFromQuote({
            fromContinue: true,
            org: payload?.org,
            contacts: payload?.contacts,
            skipPreflight: Boolean(payload?.org),
          }));
          return;
        }
        toast.error(error?.message || 'Création de facture impossible');
      } finally {
        setInvoiceBusy(false);
      }
    };
    if (options.fromContinue) {
      await run();
      return;
    }
    const { skipped } = await withActionLock(run);
    if (skipped) return;
  }

  async function createContractFromQuoteCore(signatoryContactId) {
    const payload = signatoryContactId ? { signatoryContactId } : {};
    const res = await apiService.createNr2jContractFromQuote(id, payload);
    const contract = res?.contract || res;
    toast.success(contract.number ? `Contrat créé — ${contract.number}` : 'Contrat brouillon créé');
    setLinkedContract(contract);
    navigate(`/nr2j/contrats/${contract.id}`);
  }

  async function createContractFromQuote() {
    if (linkedContract?.id) {
      navigate(`/nr2j/contrats/${linkedContract.id}`);
      return;
    }
    const run = async () => {
      setContractBusy(true);
      try {
        const cv = quote?.currentVersion || version;
        const contacts = org?.contacts || [];
        const pre = preflightQuoteToContract({
          org,
          contacts,
          quote,
          version: cv,
        });
        if (pre) {
          openContinue({ ...pre, quoteId: id, versionId: cv?.id }, (payload) => (
            createContractFromQuoteCore(payload?.signatoryContactId)
          ));
          return;
        }
        await createContractFromQuoteCore();
      } catch (error) {
        const existingId =
          error?.data?.error?.details?.existingContractId
          || error?.response?.data?.error?.details?.existingContractId
          || error?.details?.existingContractId;
        if (existingId) {
          toast.success('Contrat déjà existant pour ce devis');
          navigate(`/nr2j/contrats/${existingId}`);
          return;
        }
        const cv = quote?.currentVersion || version;
        const req = requirementsFromError(error, {
          intent: NR2J_CONTINUE_INTENTS.QUOTE_TO_CONTRACT,
          org,
          contacts: org?.contacts || [],
          quoteId: id,
          versionId: cv?.id,
        });
        if (req) {
          openContinue(req, (payload) => createContractFromQuoteCore(payload?.signatoryContactId));
          return;
        }
        toast.error(error?.message || 'Création de contrat impossible');
      } finally {
        setContractBusy(false);
      }
    };
    const { skipped } = await withActionLock(run);
    if (skipped) return;
  }

  const canNewVersion =
    currentIsSent
    || currentEffStatus === 'EXPIRED'
    || currentVersion?.status === 'REFUSED';

  const showFollowUp = !isHistoricalView
    && (currentIsSent || currentEffStatus === 'EXPIRED');

  if (loading || !quote) {
    return (
      <div className="p-8 text-sm text-gray-500" data-testid="nr2j-quote-loading">
        Chargement du devis…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="nr2j-quote-detail">
      <div
        className="sticky top-0 z-20 -mx-1 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200/80 bg-white/90 px-1 py-3 backdrop-blur"
        data-testid="nr2j-quote-sticky-actions"
      >
        <div className="min-w-0">
          <Link to="/nr2j/devis" className="inline-flex items-center gap-1 text-sm text-indigo-700">
            <ArrowLeft className="h-4 w-4" /> Retour aux devis
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">{quote.number}</h1>
            <span className="text-sm text-gray-500">
              Version actuelle : v{currentVersion?.version}
            </span>
            {versionCount > 1 ? (
              <span className="text-sm text-gray-500">{versionCount} versions</span>
            ) : null}
            <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
          </div>
          <p className="text-sm text-gray-600">
            {organizationDisplayName(org || quote.organization)}
            {org?.id ? (
              <>
                {' · '}
                <Link to={`/nr2j/clients/${org.id}`} className="text-indigo-700 hover:underline">
                  Ouvrir la fiche société
                </Link>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDraft ? (
            <>
              {canEdit ? (
                <button
                  type="button"
                  disabled={saving || actionBusy}
                  onClick={() => saveDraft()}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium"
                >
                  Enregistrer
                </button>
              ) : null}
              <button
                type="button"
                disabled={actionBusy}
                onClick={previewPdf}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium"
              >
                <Download className="mr-1 inline h-4 w-4" /> Aperçu PDF
              </button>
              {showSubmitDraft ? (
                <button
                  type="button"
                  disabled={actionBusy || !approvalInfo?.canSubmit}
                  title={approvalInfo?.canSubmit ? undefined : 'Vous n’avez pas l’autorisation de soumettre ce devis à validation'}
                  onClick={submitForApproval}
                  className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                  data-testid="nr2j-quote-submit-approval"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {approvalStatus === 'CHANGES_REQUESTED' || approvalStatus === 'REJECTED'
                    ? 'Re-soumettre pour validation'
                    : 'Soumettre pour validation'}
                </button>
              ) : null}
              {showSendDraft ? (
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={sendQuote}
                  className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
                >
                  <Send className="h-4 w-4" /> Envoyer le devis
                </button>
              ) : null}
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={actionBusy}
                onClick={downloadPdf}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <Download className="h-4 w-4" /> Télécharger PDF
              </button>
              {isSent ? (
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={resendQuote}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <Mail className="h-4 w-4" /> Renvoyer
                </button>
              ) : null}
              {canNewVersion ? (
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={newVersion}
                  className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                >
                  <RefreshCw className="h-4 w-4" /> Nouvelle version
                </button>
              ) : null}
              {showDecisionActions ? (
                <>
                  <button
                    type="button"
                    onClick={() => setDecisionOpen('accept')}
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white"
                  >
                    <Check className="h-4 w-4" /> Marquer comme accepté
                  </button>
                  <button
                    type="button"
                    onClick={() => setDecisionOpen('refuse')}
                    className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
                  >
                    <X className="h-4 w-4" /> Marquer comme refusé
                  </button>
                </>
              ) : null}
              {showCancelAction ? (
                <button
                  type="button"
                  onClick={() => setDecisionOpen('cancel')}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
                >
                  Annuler le devis
                </button>
              ) : null}
            </>
          )}
          {showFollowUp ? (
            <button
              type="button"
              onClick={() => {
                setFollowUpConflict(null);
                setFollowUpOpen(true);
              }}
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900"
            >
              Planifier une relance
            </button>
          ) : null}
          {canDeleteQuote ? (
            <button
              type="button"
              disabled={actionBusy}
              onClick={deleteQuote}
              className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
            >
              <Trash2 className="h-4 w-4" /> Supprimer le devis
            </button>
          ) : null}
          {canDeleteDraftVersion ? (
            <button
              type="button"
              disabled={actionBusy}
              onClick={deleteDraftVersion}
              className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-800"
            >
              <Trash2 className="h-4 w-4" /> Supprimer cette version brouillon
            </button>
          ) : null}
        </div>
      </div>

      {isHistoricalView ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <span>
            Vous consultez une version historique — v{version?.version}
          </span>
          <button
            type="button"
            onClick={() => selectVersion(currentVersion)}
            className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-sm font-medium text-indigo-800"
          >
            Revenir à la version actuelle
          </button>
        </div>
      ) : null}

      {showApprovalBanner && approvalPending ? (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="nr2j-quote-approval-banner"
        >
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">En attente de validation</p>
            <p>
              Soumis à {approvalUserName(latestApproval.assignedApprover)} le{' '}
              {formatNr2jDate(latestApproval.requestedAt)}
              {latestApproval.requestedBy ? ` par ${approvalUserName(latestApproval.requestedBy, '—')}` : ''}
            </p>
            {needsApproval ? (
              <p className="mt-1 text-xs text-amber-800">
                Le devis est verrouillé pendant la validation. L’envoi sera possible une fois validé.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {showApprovalBanner && approvalGranted ? (
        <div
          className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
          data-testid="nr2j-quote-approval-banner"
        >
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">
              Validé par {approvalUserName(latestApproval.decidedBy || latestApproval.assignedApprover)}
              {latestApproval.decidedAt ? ` le ${formatNr2jDate(latestApproval.decidedAt)}` : ''}
            </p>
            <p>Vous pouvez maintenant envoyer le devis au client.</p>
            {latestApproval.comment ? <p className="mt-1 text-xs">« {latestApproval.comment} »</p> : null}
          </div>
        </div>
      ) : null}

      {showApprovalBanner && approvalStatus === 'CHANGES_REQUESTED' ? (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="nr2j-quote-approval-banner"
        >
          <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">
              Modifications demandées par {approvalUserName(latestApproval.decidedBy || latestApproval.assignedApprover)}
              {latestApproval.decidedAt ? ` le ${formatNr2jDate(latestApproval.decidedAt)}` : ''}
            </p>
            {latestApproval.comment ? <p>Motif : {latestApproval.comment}</p> : null}
            <p className="mt-1 text-xs text-amber-800">
              Modifiez le devis puis re-soumettez-le pour validation.
            </p>
          </div>
        </div>
      ) : null}

      {showApprovalBanner && approvalStatus === 'REJECTED' ? (
        <div
          className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
          data-testid="nr2j-quote-approval-banner"
        >
          <X className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">
              Refusé par {approvalUserName(latestApproval.decidedBy || latestApproval.assignedApprover)}
              {latestApproval.decidedAt ? ` le ${formatNr2jDate(latestApproval.decidedAt)}` : ''}
            </p>
            {latestApproval.comment ? <p>Motif : {latestApproval.comment}</p> : null}
          </div>
        </div>
      ) : null}

      {orgBannerMissing.length > 0 ? (
        <div
          className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
          data-testid="nr2j-quote-org-missing"
        >
          <p className="font-semibold">Informations société à compléter</p>
          <ul className="mt-2 space-y-1">
            {orgBannerMissing.map((key) => (
              <li key={key}>
                ✗ {NR2J_QUOTE_SEND_GATE_LABELS[key] || NR2J_CONVERSION_MISSING_LABELS[key] || key}
              </li>
            ))}
          </ul>
          {org?.id ? (
            <Link
              to={buildOrgFicheUrlFromConversion({
                organizationId: org.id,
                quoteId: id,
                missingKeys: orgBannerMissing,
                intent: currentIsAccepted ? 'invoice' : 'quote',
              }) || `/nr2j/clients/${org.id}`}
              className="mt-3 inline-flex rounded-lg bg-rose-700 px-3 py-2 text-sm font-medium text-white"
            >
              Compléter la fiche société
            </Link>
          ) : null}
        </div>
      ) : null}

      {isSent && !isHistoricalView ? (
        <div
          className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900"
          data-testid="nr2j-quote-commercial-status"
        >
          <p className="font-semibold">Statut commercial : Envoyé</p>
          <p className="mt-1 text-xs text-sky-800">
            En attente du retour client (lien email sécurisé ou enregistrement manuel).
          </p>
        </div>
      ) : null}

      {isAccepted && !isHistoricalView ? (
        <div className="space-y-3">
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
            data-testid="nr2j-quote-commercial-status"
          >
            {quote?.acceptanceSource === 'CLIENT_ONLINE' ? (
              <>
                <p className="font-semibold">
                  Accepté par le client
                  {version?.acceptedAt ? ` le ${formatNr2jDate(version.acceptedAt)}` : ''}
                </p>
                <p className="mt-1 text-xs text-emerald-800">
                  Acceptation enregistrée en ligne via le lien sécurisé du devis.
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold">
                  Acceptation client enregistrée manuellement
                  {quote?.decisionRecordedBy ? ` par ${quote.decisionRecordedBy}` : ''}
                  {version?.acceptedAt ? ` le ${formatNr2jDate(version.acceptedAt)}` : ''}
                </p>
                <p className="mt-1 text-xs text-emerald-800">
                  Saisie commerciale (téléphone, accord oral, email externe, etc.).
                </p>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            <span>
              {linkedInvoice
                ? `Facture liée : ${linkedInvoice.number || 'brouillon'}`
                : 'Devis accepté — vous pouvez créer la facture.'}
            </span>
            {linkedInvoice ? (
              <Link
                to={`/nr2j/facturation/${linkedInvoice.id}`}
                className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-sm font-medium text-indigo-800"
                data-testid="nr2j-quote-open-invoice"
              >
                Ouvrir la facture
              </Link>
            ) : canCreateInvoiceFromQuote ? (
              <button
                type="button"
                disabled={invoiceBusy || actionBusy}
                onClick={createInvoiceFromQuote}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                data-testid="nr2j-quote-create-invoice"
              >
                Créer la facture
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-950">
            <span>
              {linkedContract
                ? `Contrat lié : ${linkedContract.number || 'brouillon'}`
                : 'Devis accepté — vous pouvez créer le contrat.'}
            </span>
            {linkedContract ? (
              <Link
                to={`/nr2j/contrats/${linkedContract.id}`}
                className="rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-sm font-medium text-violet-900"
                data-testid="nr2j-quote-open-contract"
              >
                Ouvrir le contrat
              </Link>
            ) : canCreateContractFromQuote ? (
              <button
                type="button"
                disabled={contractBusy || actionBusy}
                onClick={createContractFromQuote}
                className="rounded-lg bg-violet-700 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                data-testid="nr2j-quote-create-contract"
              >
                Créer le contrat
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {isRefused && !isHistoricalView ? (
        <div
          className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
          data-testid="nr2j-quote-commercial-status"
        >
          {quote?.refusalSource === 'CLIENT_ONLINE' ? (
            <>
              <p className="font-semibold">
                Refusé par le client
                {version?.refusedAt ? ` le ${formatNr2jDate(version.refusedAt)}` : ''}
              </p>
              <p className="mt-1 text-xs text-rose-800">
                Refus enregistré en ligne via le lien sécurisé du devis.
              </p>
            </>
          ) : (
            <p className="font-semibold">
              Refus client
              {quote?.decisionRecordedBy ? ` — enregistré par ${quote.decisionRecordedBy}` : ' — enregistré manuellement'}
              {version?.refusedAt ? ` le ${formatNr2jDate(version.refusedAt)}` : ''}
            </p>
          )}
          {version?.decisionNote ? (
            <p className="mt-1 text-xs">Motif : {version.decisionNote}</p>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Lignes</h2>
            {canEdit ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <select
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) {
                      addCatalogLine(e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">Ajouter depuis le catalogue…</option>
                  {catalog.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.code} — {item.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setLines((prev) => [...prev, emptyLine()])}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <Plus className="h-4 w-4" /> Ligne libre
                </button>
              </div>
            ) : null}

            <div className="mt-4 space-y-4">
              {lines.length === 0 ? (
                <p className="text-sm text-gray-500">Aucune ligne.</p>
              ) : (
                lines.map((line, idx) => {
                  const lineTotals = computeQuoteLineTotals(lineForTotals(line));
                  return (
                    <div key={line.key} className="rounded-lg border border-gray-100 p-3">
                      <div className="grid gap-2 sm:grid-cols-2">
                        <label className="block text-sm sm:col-span-2">
                          <span className="text-gray-500">Désignation *</span>
                          <input
                            required
                            disabled={!canEdit}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                            value={line.name}
                            onChange={(e) => setLines((prev) => prev.map((l, i) => (
                              i === idx ? { ...l, name: e.target.value } : l
                            )))}
                          />
                        </label>
                        <label className="block text-sm">
                          <span className="text-gray-500">Qté</span>
                          <input
                            type="number"
                            min="1"
                            disabled={!canEdit}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                            value={line.quantity}
                            onChange={(e) => setLines((prev) => prev.map((l, i) => (
                              i === idx ? { ...l, quantity: e.target.value } : l
                            )))}
                          />
                        </label>
                        <label className="block text-sm">
                          <span className="text-gray-500">Prix unitaire HT (€)</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            disabled={!canEdit}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                            value={line.priceEuros}
                            onChange={(e) => setLines((prev) => prev.map((l, i) => (
                              i === idx ? { ...l, priceEuros: e.target.value } : l
                            )))}
                          />
                        </label>
                        <label className="block text-sm">
                          <span className="text-gray-500">TVA</span>
                          <select
                            disabled={!canEdit}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                            value={line.vatRateBps}
                            onChange={(e) => setLines((prev) => prev.map((l, i) => (
                              i === idx ? { ...l, vatRateBps: Number(e.target.value) } : l
                            )))}
                          >
                            {NR2J_VAT_OPTIONS.map((v) => (
                              <option key={v.bps} value={v.bps}>{v.label}</option>
                            ))}
                          </select>
                        </label>
                        <label className="block text-sm">
                          <span className="text-gray-500">Facturation</span>
                          <select
                            disabled={!canEdit}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                            value={line.billingType}
                            onChange={(e) => setLines((prev) => prev.map((l, i) => (
                              i === idx ? { ...l, billingType: e.target.value } : l
                            )))}
                          >
                            {NR2J_CATALOG_BILLING_TYPES.map((t) => (
                              <option key={t.id} value={t.id}>{t.label}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                        <span>
                          HT {formatCentsAsEuros(lineTotals.lineHtCents)} € · TVA{' '}
                          {formatCentsAsEuros(lineTotals.lineVatCents)} € · TTC{' '}
                          {formatCentsAsEuros(lineTotals.lineTtcCents)} €
                          {!isDraft ? ` · ${nr2jBillingTypeLabel(line.billingType)}` : ''}
                        </span>
                        {canEdit ? (
                          <button
                            type="button"
                            onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-rose-700"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          {lines.length > 0 ? (
            <section
              className="rounded-xl border border-indigo-100 bg-indigo-50/30 p-5 shadow-sm"
              data-testid="nr2j-quote-discounts-section"
            >
              <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-900">
                Remises / avantages commerciaux
              </h2>
              <p className="mt-1 text-xs text-indigo-800/80">
                La portée de chaque remise suit la fréquence de facturation de la ligne.
              </p>
              <div className="mt-4 space-y-4">
                {lines.map((line, idx) => {
                  const totals = computeQuoteLineTotals(lineForTotals(line));
                  const suffix = nr2jBillingPeriodSuffix(line.billingType);
                  const scope = nr2jDiscountScopeLabel(line.billingType);
                  const unitCents = eurosToCents(line.priceEuros);
                  const unitInitial = formatCentsAsEuros(unitCents);
                  const unitAfter = formatCentsAsEuros(
                    Math.round(totals.lineHtCents / Math.max(1, Number(line.quantity) || 1)),
                  );
                  return (
                    <div
                      key={line.key}
                      className="rounded-lg border border-indigo-100 bg-white p-3"
                      data-testid="nr2j-quote-line-discount"
                    >
                      <p className="text-sm font-medium text-gray-900">
                        {line.name?.trim() || `Ligne ${idx + 1}`}
                      </p>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <label className="block text-sm">
                          <span className="text-gray-500">Type de remise</span>
                          <select
                            disabled={!canEdit}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                            value={line.discountMode}
                            onChange={(e) => {
                              const mode = e.target.value;
                              setLines((prev) => prev.map((l, i) => (
                                i === idx
                                  ? {
                                    ...l,
                                    discountMode: mode,
                                    discountPercent: mode === 'percent' ? l.discountPercent : '',
                                    discountEuros: mode === 'fixed' ? l.discountEuros : '',
                                  }
                                  : l
                              )));
                            }}
                          >
                            <option value="none">Aucune</option>
                            <option value="percent">Pourcentage</option>
                            <option value="fixed">Montant fixe HT</option>
                          </select>
                        </label>
                        {line.discountMode === 'percent' ? (
                          <label className="block text-sm">
                            <span className="text-gray-500">Remise (%)</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              disabled={!canEdit}
                              className="mt-1 w-full rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-2 disabled:bg-gray-50"
                              value={line.discountPercent}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx
                                  ? {
                                    ...l,
                                    discountMode: e.target.value === '' || Number(e.target.value) === 0
                                      ? 'none'
                                      : 'percent',
                                    discountPercent: e.target.value,
                                    discountEuros: '',
                                  }
                                  : l
                              )))}
                            />
                          </label>
                        ) : null}
                        {line.discountMode === 'fixed' ? (
                          <label className="block text-sm" data-testid="nr2j-quote-line-discount-fixed">
                            <span className="text-gray-500">Remise montant fixe HT (€)</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              disabled={!canEdit}
                              className="mt-1 w-full rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-2 disabled:bg-gray-50"
                              value={line.discountEuros}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx
                                  ? {
                                    ...l,
                                    discountMode: e.target.value === '' || Number(e.target.value) === 0
                                      ? 'none'
                                      : 'fixed',
                                    discountEuros: e.target.value,
                                    discountPercent: '',
                                  }
                                  : l
                              )))}
                            />
                          </label>
                        ) : null}
                      </div>
                      <dl className="mt-3 grid gap-1 text-xs text-gray-600 sm:grid-cols-2">
                        <div>
                          <dt className="text-gray-500">Prix initial</dt>
                          <dd className="font-medium text-gray-900">{unitInitial} € HT{suffix}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Portée</dt>
                          <dd className="font-medium text-gray-900">{scope}</dd>
                        </div>
                        {totals.discountHtCents > 0 ? (
                          <>
                            <div>
                              <dt className="text-gray-500">Remise commerciale</dt>
                              <dd className="font-medium text-indigo-800">
                                {line.discountMode === 'percent'
                                  ? `${line.discountPercent || 0} %`
                                  : `${formatCentsAsEuros(totals.discountHtCents)} € HT`}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-gray-500">Prix après remise</dt>
                              <dd className="font-semibold text-emerald-800">
                                {unitAfter} € HT{suffix}
                              </dd>
                            </div>
                          </>
                        ) : (
                          <div className="sm:col-span-2">
                            <dt className="text-gray-500">Prix après remise</dt>
                            <dd className="font-medium text-gray-700">
                              {unitInitial} € HT{suffix} (aucune remise)
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Notes</h2>
            <div className="mt-3 grid gap-3">
              <label className="block text-sm">
                <span className="text-gray-500">Note client (visible sur le devis)</span>
                <textarea
                  rows={2}
                  disabled={!canEdit}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                  value={clientNote}
                  onChange={(e) => setClientNote(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-500">Note interne</span>
                <textarea
                  rows={2}
                  disabled={!canEdit}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                />
              </label>
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Paramètres</h2>
            <div className="mt-3 space-y-3">
              <label className="block text-sm">
                <span className={
                  fieldErrors.contact
                    ? 'font-medium text-rose-700'
                    : contactId
                      ? 'font-medium text-emerald-700'
                      : 'text-gray-500'
                }
                >
                  Contact
                </span>
                <select
                  disabled={!canEdit}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 disabled:bg-gray-50 ${
                    fieldErrors.contact
                      ? 'border-rose-500 bg-rose-50'
                      : contactId
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-300'
                  }`}
                  value={contactId}
                  onChange={(e) => {
                    setContactId(e.target.value);
                    setFieldErrors((prev) => {
                      if (!prev.contact) return prev;
                      const next = { ...prev };
                      delete next.contact;
                      return next;
                    });
                    setPersistentMissing((prev) => prev.filter((k) => k !== 'recipientEmail'));
                  }}
                >
                  <option value="">Par défaut (email société)</option>
                  {(org?.contacts || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {contactDisplayName(c) || c.id}
                      {c.email ? ` (${c.email})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className={
                  fieldErrors.validUntil
                    ? 'font-medium text-rose-700'
                    : validUntil
                      ? 'font-medium text-emerald-700'
                      : 'text-gray-500'
                }
                >
                  Validité jusqu’au
                </span>
                <input
                  type="date"
                  disabled={!canEdit}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 disabled:bg-gray-50 ${
                    fieldErrors.validUntil
                      ? 'border-rose-500 bg-rose-50'
                      : validUntil
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-300'
                  }`}
                  value={validUntil}
                  onChange={(e) => {
                    setValidUntil(e.target.value);
                    setFieldErrors((prev) => {
                      if (!prev.validUntil) return prev;
                      const next = { ...prev };
                      delete next.validUntil;
                      return next;
                    });
                    setPersistentMissing((prev) => prev.filter((k) => k !== 'validUntil'));
                  }}
                />
                {fieldErrors.validUntil ? (
                  <span className="mt-1 block text-xs text-rose-700">Date de validité obligatoire</span>
                ) : validUntil ? (
                  <span className="mt-1 block text-xs font-medium text-emerald-700">Champ valide</span>
                ) : null}
              </label>
            </div>
          </div>

          <Nr2jQuoteLivePreviewPanel lines={lines} lineToPayload={lineToPayload} />
          {!isDraft && version ? (
            <p className="-mt-2 px-1 text-xs text-gray-400">
              Enregistré : HT {formatCentsAsEuros(version.totalHtCents)} € · TTC{' '}
              {formatCentsAsEuros(version.totalTtcCents)} €
            </p>
          ) : null}

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Versions</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {(quote.versions || []).map((v) => {
                const vEff = v.effectiveStatus || effectiveStatus(v);
                const vBadge = nr2jQuoteStatusBadge(vEff);
                const selected = v.id === version?.id;
                return (
                  <li key={v.id}>
                    <button
                      type="button"
                      data-testid="nr2j-quote-version-item"
                      onClick={() => selectVersion(v)}
                      className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                        selected
                          ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200'
                          : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">v{v.version}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-xs ${vBadge.className}`}>
                          {vBadge.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {v.sentAt ? `Envoyé ${formatNr2jDate(v.sentAt)}` : 'Non envoyé'}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Emails</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {(version?.emailLogs || []).length === 0 ? (
                <li className="text-gray-500">Aucun envoi email.</li>
              ) : (
                (version?.emailLogs || []).map((log) => (
                  <li key={log.id} className="rounded-lg border border-gray-100 px-3 py-2">
                    <p className="text-gray-900">{log.recipient || '—'}</p>
                    <p className="text-xs text-gray-500">
                      v{version?.version}
                      {' · '}
                      {formatNr2jDate(log.createdAt || log.sentAt)}
                      {log.status ? ` · ${nr2jEmailLogStatusLabel(log.status)}` : ''}
                    </p>
                  </li>
                ))
              )}
            </ul>
          </div>
        </aside>
      </div>

      {sendGateOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            aria-label="Fermer"
            onClick={() => setSendGateOpen(false)}
          />
          <div className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Envoi impossible</h3>
            <p className="mt-2 text-sm text-gray-600">
              Complétez les informations manquantes avant d’envoyer le devis :
            </p>
            <ul className="mt-3 space-y-1 text-sm text-rose-700">
              {sendGateMissing.map((key) => (
                <li key={key}>✗ {NR2J_QUOTE_SEND_GATE_LABELS[key] || key}</li>
              ))}
            </ul>
            {sendGateMissing.some((k) => NR2J_QUOTE_ORG_GATE_KEYS.has(k)) && org?.id ? (
              <Link
                to={buildOrgFicheUrlFromQuoteGate({
                  organizationId: org.id,
                  quoteId: id,
                  missingKeys: sendGateMissing,
                }) || `/nr2j/clients/${org.id}`}
                className="mt-4 block w-full rounded-lg bg-indigo-600 px-3 py-2 text-center text-sm font-medium text-white"
              >
                Compléter la fiche société
              </Link>
            ) : null}
            <button
              type="button"
              onClick={() => setSendGateOpen(false)}
              className="mt-2 w-full rounded-lg border px-3 py-2 text-sm"
            >
              Fermer
            </button>
          </div>
        </div>
      ) : null}

      {decisionOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            aria-label="Fermer"
            onClick={() => setDecisionOpen(null)}
          />
          <div className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              {decisionOpen === 'accept'
                ? 'Enregistrer l’acceptation client'
                : decisionOpen === 'refuse'
                  ? 'Enregistrer le refus client'
                  : 'Annuler le devis'}
            </h3>
            {decisionOpen === 'accept' || decisionOpen === 'refuse' ? (
              <p className="mt-2 text-sm text-gray-600">
                Le client a répondu hors Clickorico (mail, téléphone, oral…).
                Vous enregistrez ici le résultat commercial — ce n’est pas une validation en ligne.
              </p>
            ) : null}
            <label className="mt-3 block text-sm">
              <span className="text-gray-500">
                {decisionOpen === 'refuse' ? 'Motif / note (facultatif)' : 'Note (optionnelle)'}
              </span>
              <textarea
                rows={2}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={decisionNote}
                onChange={(e) => setDecisionNote(e.target.value)}
              />
            </label>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                disabled={actionBusy}
                onClick={() => submitDecision(decisionOpen)}
                className="flex-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
              >
                Confirmer
              </button>
              <button
                type="button"
                onClick={() => setDecisionOpen(null)}
                className="rounded-lg border px-3 py-2 text-sm"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {org ? (
        <Nr2jPlanFollowUpModal
          open={followUpOpen}
          busy={followUpBusy}
          organizationName={organizationDisplayName(org)}
          existingConflict={followUpConflict}
          onClose={() => {
            setFollowUpOpen(false);
            setFollowUpConflict(null);
          }}
          onSubmit={async (payload) => {
            setFollowUpBusy(true);
            try {
              await apiService.planNr2jFollowUp(org.id, payload);
              toast.success('Relance planifiée');
              setFollowUpOpen(false);
              setFollowUpConflict(null);
            } catch (error) {
              const existingTask = error?.data?.error?.existingTask;
              if (existingTask || error?.data?.error?.code === 'FOLLOWUP_EXISTS') {
                setFollowUpConflict({ message: error.message, existingTask });
              } else {
                toast.error(error?.message || 'Échec');
              }
            } finally {
              setFollowUpBusy(false);
            }
          }}
          onUpdateExisting={async (payload) => {
            setFollowUpBusy(true);
            try {
              await apiService.planNr2jFollowUp(org.id, { ...payload, updateExisting: true });
              toast.success('Relance modifiée');
              setFollowUpOpen(false);
              setFollowUpConflict(null);
            } catch (error) {
              toast.error(error?.message || 'Échec');
            } finally {
              setFollowUpBusy(false);
            }
          }}
        />
      ) : null}

      <Nr2jContinueModal
        open={Boolean(continueReq)}
        requirements={continueReq?.requirements}
        onClose={closeContinue}
        onSaved={async (payload) => {
          if (payload?.org) {
            setOrg((prev) => ({
              ...(prev || {}),
              ...payload.org,
              contacts: payload.contacts || prev?.contacts,
            }));
          } else if (payload?.contacts?.length) {
            setOrg((prev) => (prev ? { ...prev, contacts: payload.contacts } : prev));
          }
          const retry = continueReq?.retry;
          closeContinue();
          if (!retry) return;
          try {
            await runContinueRetry(retry, payload);
          } catch (error) {
            toast.error(error?.message || 'Reprise impossible');
          }
        }}
      />

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
